/**
 * RaptorQ Service - Transparent worker offload with main-thread fallback
 * 
 * Wraps RaptorQ encode/decode operations behind a unified async API.
 * If a Web Worker is available, CPU-intensive WASM work runs off the main thread.
 * If the worker fails to load (unsupported browser, CSP, etc.), falls back
 * to the existing inline WASM calls — zero behavior change, same results.
 * 
 * Usage:
 *   import { raptorQService } from "@/services/raptorq-service";
 *   await raptorQService.init();
 *   const symbols = await raptorQService.encode(chunk, MTU, count);
 */

import * as raptorq from "raptorq";

// ============================================================
// Types
// ============================================================

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  // P0-S: Timer handle so we can clear it on successful resolve/reject —
  // without this, a slow-but-alive worker triggers a spurious timeout reject
  // AFTER the real response arrives, and the second settle is a no-op but
  // the timer's setTimeout closure keeps retaining the resolve/reject
  // references (small leak, but matters for long transfers).
  timer?: ReturnType<typeof setTimeout>;
};

// P0-S: Timeout for worker round-trip. RaptorQ encode of a 32 MB chunk
// typically finishes in 50-200 ms on desktop. 30 s is generous enough to
// cover main-thread storms AND catches the "worker crashed silently"
// failure mode (observed in 4/24 Brave↔Brave test where the worker
// onmessage threw TypeError on null e.data and never posted a response,
// hanging the main thread's await raptorQService.encode() indefinitely
// and stalling every FILE_CHUNK_REQUEST response behind it).
const WORKER_REQUEST_TIMEOUT_MS = 30_000;

// ============================================================
// Service Implementation
// ============================================================

// Default batch sizes — adapted at runtime by connection quality
const DEFAULT_BATCH_SIZE = 50;
const MIN_BATCH_SIZE = 5;
const MAX_BATCH_SIZE = 200;

class RaptorQService {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 0;
  private initPromise: Promise<void> | null = null;

  // Main-thread WASM fallback (mirrors provider.tsx ensureRaptorQ)
  private mainThreadInitPromise: Promise<any> | null = null;

  // ============================================================
  // Initialization
  // ============================================================

  /**
   * Initialize the service. Safe to call multiple times — subsequent calls
   * return the same promise. Spawns the worker and warms up main-thread WASM.
   */
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    // Always warm up main-thread WASM (needed for fallback + decoder paths)
    const mainThreadReady = this.ensureMainThread();

    // Attempt to spawn worker in parallel
    try {
      await this.spawnWorker();
    } catch (e) {
      console.warn('[RaptorQService] Worker spawn failed, using main-thread only', e);
    }

    // Ensure main thread WASM is ready regardless
    await mainThreadReady;

    console.log(`[RaptorQService] Initialized — worker: ${this.workerAvailable ? 'YES' : 'NO (main-thread fallback)'}`);
  }

  private async spawnWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      throw new Error('Web Workers not supported');
    }

    return new Promise<void>((resolve, reject) => {
      // P0-I Phase 1S: 30 s timeout + 5 s progress logging (was 5 s
      // hard timeout).  See merkle-tree-worker-service.ts for the full
      // rationale.  Particularly important for RaptorQ because it
      // needs WASM init inside the worker — longer startup than the
      // pure-JS workers, and even more painful if we fall back to
      // main thread (all erasure coding lands on the UI thread).
      const INIT_TIMEOUT_MS = 30000;
      const start = performance.now();
      const progressInterval = setInterval(() => {
        const elapsed = Math.round((performance.now() - start) / 1000);
        console.warn(
          `[RaptorQService] Worker init still pending after ${elapsed}s (will timeout at 30s) — main thread likely saturated`,
        );
      }, 5000);
      const timeout = setTimeout(() => {
        clearInterval(progressInterval);
        reject(new Error(`Worker init timed out after ${INIT_TIMEOUT_MS / 1000}s`));
      }, INIT_TIMEOUT_MS);

      try {
        // Worker is built as a separate entry point by build.ts → /raptorq-worker.js
        this.worker = new Worker('/raptorq-worker.js', { type: 'module' });

        this.worker.onmessage = (e: MessageEvent) => {
          this.handleMessage(e);
        };

        this.worker.onerror = (e: ErrorEvent) => {
          console.warn('[RaptorQService] Worker error:', e.message);
          clearInterval(progressInterval);
          clearTimeout(timeout);
          this.teardownWorker();
          reject(new Error(e.message));
        };

        // Send init command and wait for WASM ready
        const initId = this.nextRequestId++;
        this.pendingRequests.set(initId, {
          resolve: () => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.workerAvailable = true;
            const elapsed = Math.round((performance.now() - start) / 1000);
            if (elapsed >= 5) {
              console.log(
                `[RaptorQService] Worker init completed after ${elapsed}s (slow but successful)`,
              );
            }
            resolve();
          },
          reject: (err) => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.teardownWorker();
            reject(err);
          }
        });

        this.worker.postMessage({ task: 'init', id: initId });
      } catch (e) {
        clearInterval(progressInterval);
        clearTimeout(timeout);
        reject(e);
      }
    });
  }

  private teardownWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerAvailable = false;
    // Reject any pending requests
    for (const [, pending] of this.pendingRequests) {
      pending.reject(new Error('Worker terminated'));
    }
    this.pendingRequests.clear();
  }

  private async ensureMainThread(): Promise<void> {
    if (this.mainThreadInitPromise) {
      await this.mainThreadInitPromise;
      return;
    }
    this.mainThreadInitPromise = raptorq.default(require("raptorq/raptorq_bg.wasm"));
    await this.mainThreadInitPromise;
  }

  // ============================================================
  // Worker Communication
  // ============================================================

  private handleMessage(e: MessageEvent): void {
    const { id, error, ...result } = e.data;
    const pending = this.pendingRequests.get(id);
    if (!pending) return;
    this.pendingRequests.delete(id);
    // P0-S: Clear the watchdog timer so it can't fire a spurious timeout
    // after the worker responded (and release the closure's retained refs).
    if (pending.timer) clearTimeout(pending.timer);

    if (error) {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  }

  private sendToWorker(task: string, data: Record<string, any>, transferables?: Transferable[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.nextRequestId++;
      // P0-S: Install a watchdog so a silently-crashed worker (e.g. the null
      // e.data TypeError observed on 4/24) rejects the caller's promise
      // instead of hanging forever. The caller (encode/decode) catches
      // rejection and falls back to main-thread WASM, which keeps the
      // transfer alive at somewhat reduced throughput.
      const timer = setTimeout(() => {
        const p = this.pendingRequests.get(id);
        if (!p) return;
        this.pendingRequests.delete(id);
        console.warn(
          `[RaptorQService] Worker request ${task}#${id} timed out after ${WORKER_REQUEST_TIMEOUT_MS}ms — main-thread fallback will be attempted`,
        );
        p.reject(new Error(`Worker request timeout (${task})`));
      }, WORKER_REQUEST_TIMEOUT_MS);
      this.pendingRequests.set(id, { resolve, reject, timer });

      const message = { task, id, ...data };
      try {
        this.worker.postMessage(message, transferables || []);
      } catch (postErr) {
        // Structured-clone failure (e.g. detached ArrayBuffer) throws
        // synchronously from postMessage. Clean up so we don't leak the
        // pending entry + timer, and reject the caller.
        clearTimeout(timer);
        this.pendingRequests.delete(id);
        reject(postErr instanceof Error ? postErr : new Error(String(postErr)));
      }
    });
  }

  // ============================================================
  // Public API — Encoding (Phase 1: integrated into provider.tsx)
  // ============================================================

  /**
   * Encode a data chunk into RaptorQ repair symbols.
   * Routes to worker if available, otherwise encodes on main thread.
   *
   * @param chunk - Raw chunk data to encode
   * @param mtu - Maximum transmission unit (symbol size)
   * @param count - Number of symbols to generate
   * @param opts.transfer - When true, transfer `chunk.buffer` to the worker
   *   (zero-copy). The caller MUST NOT read `chunk` after awaiting this call.
   *   When false (default) the chunk is structured-cloned across the worker
   *   boundary — safe for callers that still use `chunk` afterwards, but
   *   costs one full chunk-sized copy per encode. See P0-T history below.
   * @returns Array of repair symbols
   */
  async encode(
    chunk: Uint8Array,
    mtu: number,
    count: number,
    opts?: { transfer?: boolean },
  ): Promise<Uint8Array[]> {
    const useTransfer = opts?.transfer === true;
    if (this.workerAvailable) {
      try {
        // P0-T (4/24): Pass chunk.buffer as a Transferable when the caller
        // opts in. Without this, every encode becomes a 32 MB structured-
        // clone copy across the main→worker boundary. For a 33 GB
        // Brave↔Brave test that accumulates ~14.8 GB of cumulative copy
        // traffic by chunk 462, at which point Brave's worker postMessage
        // pipeline starts delivering `e.data === null` back to the worker
        // and the transfer wedges at ~44%. With transfer=true the boundary
        // is zero-copy and the failure mode does not reproduce.
        //
        // REGRESSION HISTORY: An earlier comment here claimed transfer was
        // unsafe because "the caller still uses `chunk` after encode() for
        // maxSymbols/maxLoops calculation." That pattern no longer exists
        // at any caller — grep `raptorQService.encode` in provider.tsx;
        // all four call sites compute symbolsToGenerate BEFORE the await,
        // not after. The "fix" to disable transfer introduced a copy tax
        // that scaled linearly with file size and masquerades as a Brave
        // storage / worker bug. It is a self-inflicted wound.
        //
        // Worker transfers the symbols array back via Transferable
        // regardless of this flag (zero-copy return).
        const transferables = useTransfer ? [chunk.buffer] : undefined;
        const result = await this.sendToWorker('encode', { chunk, mtu, count }, transferables);
        return result.symbols as Uint8Array[];
      } catch (e) {
        if (useTransfer) {
          // chunk.buffer was transferred to the worker and is now detached
          // on this thread. The main-thread fallback below would encode a
          // zero-byte buffer and produce no symbols, so surface the error
          // to the caller instead. FILE_CHUNK_REQUEST / serveGroupChunkToPeer
          // already handle encode rejection by sending `chunk: undefined`
          // back to the receiver, which retries via CHUNK_REQUEST_TIMEOUT.
          console.warn(
            '[RaptorQService] Worker encode failed after buffer transfer; main-thread fallback skipped (caller will handle retry)',
            e,
          );
          throw e;
        }
        console.warn('[RaptorQService] Worker encode failed, falling back to main thread', e);
        // Fall through to main-thread path (copy-mode only — chunk still intact)
      }
    }

    // Main-thread fallback — reachable only when (a) worker unavailable or
    // (b) worker rejected AND caller did not transfer (chunk still intact).
    await this.ensureMainThread();
    const encoder = raptorq.Encoder.with_defaults(chunk, mtu);
    const symbols = encoder.encode(count) as unknown as Uint8Array[];
    encoder.free();
    return symbols;
  }

  // ============================================================
  // Public API — Decoding (Phase 2: ready for future integration)
  // ============================================================

  /**
   * Create a decoder context. When using the worker, the decoder lives
   * in the worker thread. When using main thread, returns a local decoder.
   * 
   * @param decoderId - Unique ID for this decoder (e.g., downloadNonce)
   * @param chunkSize - Expected size of the decoded chunk
   * @param mtu - Symbol size
   * @returns A decoder handle (worker-backed or local)
   */
  async createDecoder(
    decoderId: string,
    chunkSize: number,
    mtu: number
  ): Promise<DecoderHandle> {
    if (this.workerAvailable) {
      try {
        await this.sendToWorker('decode-create', { decoderId, chunkSize, mtu });
        let completed = false;
        let batchSize = DEFAULT_BATCH_SIZE;
        // Accumulation buffer — flushed to worker as a single postMessage
        let symbolBuffer: Uint8Array[] = [];
        let flushScheduled = false;
        // Pending callers waiting for their addSymbol result
        let pendingResolvers: Array<(value: Uint8Array | undefined) => void> = [];
        let totalSymbolsQueued = 0;

        const flushBatch = async () => {
          flushScheduled = false;
          if (symbolBuffer.length === 0 || completed) return;
          // Snapshot and reset buffer before async call
          const batch = symbolBuffer;
          const resolvers = pendingResolvers;
          symbolBuffer = [];
          pendingResolvers = [];

          try {
            // Transfer symbol buffers to worker (zero-copy)
            const transferables = batch.map(s => s.buffer);
            const result = await this.sendToWorker(
              'decode-add-batch',
              { decoderId, symbols: batch },
              transferables
            );

            if (result.complete) {
              completed = true;
              const decodedChunk = result.chunk as Uint8Array;
              const completedAtIndex = result.completedAtIndex as number;
              // Resolve the caller whose symbol triggered completion
              for (let i = 0; i < resolvers.length; i++) {
                resolvers[i](i === completedAtIndex ? decodedChunk : undefined);
              }
            } else {
              // No completion — resolve all with undefined
              for (const r of resolvers) r(undefined);
            }
          } catch (e) {
            // On worker error, resolve all pending with undefined.
            // This commonly fires when a chunk was already decoded and free()'d
            // but an in-flight batch flush returns after the worker decoder is gone.
            // Only warn if the decoder wasn't already completed (unexpected error).
            if (!completed) {
              console.warn('[RaptorQService] Batch decode failed:', e);
            }
            completed = true;
            for (const r of resolvers) r(undefined);
          }
        };

        return {
          isWorkerBacked: true,
          id: decoderId,
          addSymbol: (symbol: Uint8Array): Promise<Uint8Array | undefined> => {
            if (completed) return Promise.resolve(undefined);
            return new Promise<Uint8Array | undefined>((resolve) => {
              symbolBuffer.push(symbol);
              pendingResolvers.push(resolve);
              totalSymbolsQueued++;

              // Flush when batch is full
              if (symbolBuffer.length >= batchSize) {
                flushBatch();
              } else if (!flushScheduled) {
                // Schedule microtask flush for partial batches
                // (handles tail symbols at end of chunk or slow connections)
                flushScheduled = true;
                queueMicrotask(() => {
                  if (symbolBuffer.length > 0 && !completed) {
                    flushBatch();
                  } else {
                    flushScheduled = false;
                  }
                });
              }
            });
          },
          free: () => {
            completed = true;
            // Resolve any lingering callers
            for (const r of pendingResolvers) r(undefined);
            pendingResolvers = [];
            symbolBuffer = [];
            this.workerFreeDecoder(decoderId);
          },
          setBatchSize: (size: number) => {
            batchSize = Math.max(MIN_BATCH_SIZE, Math.min(MAX_BATCH_SIZE, size));
          },
        };
      } catch (e) {
        console.warn('[RaptorQService] Worker createDecoder failed, using local decoder', e);
      }
    }

    // Main-thread fallback — create local decoder (no batching needed, already fast)
    await this.ensureMainThread();
    const decoder = raptorq.Decoder.with_defaults(BigInt(chunkSize), mtu);
    let localCompleted = false;
    return {
      isWorkerBacked: false,
      id: decoderId,
      addSymbol: (symbol: Uint8Array) => {
        if (localCompleted) return Promise.resolve(undefined);
        const result = decoder.add(symbol);
        if (result !== undefined) localCompleted = true;
        return Promise.resolve(result);
      },
      free: () => {
        localCompleted = true;
        try { decoder.free(); } catch { /* already freed */ }
      },
      setBatchSize: () => { /* no-op for main-thread decoder */ },
    };
  }

  private async workerFreeDecoder(decoderId: string): Promise<void> {
    try {
      await this.sendToWorker('decode-free', { decoderId });
    } catch {
      // Silent — decoder may already be freed
    }
  }

  // ============================================================
  // Status
  // ============================================================

  /** Whether the worker is active and encoding/decoding off the main thread */
  isUsingWorker(): boolean {
    return this.workerAvailable;
  }

  /** Tear down the worker. Call on unmount if needed. */
  destroy(): void {
    this.teardownWorker();
    this.initPromise = null;
  }
}

// ============================================================
// Decoder Handle Interface
// ============================================================

export interface DecoderHandle {
  /** Whether this decoder runs in a Web Worker */
  isWorkerBacked: boolean;
  /** Caller-assigned ID */
  id: string;
  /** Feed a symbol. Returns decoded chunk when complete, undefined otherwise. */
  addSymbol(symbol: Uint8Array): Promise<Uint8Array | undefined>;
  /** Free WASM resources */
  free(): void;
  /** Adapt batch size based on connection quality (higher = fewer postMessages, better throughput) */
  setBatchSize(size: number): void;
}

// ============================================================
// Singleton Export
// ============================================================

export const raptorQService = new RaptorQService();
